import { useState, useEffect } from 'react'
import { Table, Button, Badge, Modal, Form, Row, Col, Card } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { ringGroupsApi, extensionsApi, mohApi } from '../services/api'
import { DestinationSelector } from '../components/common'
import { useConfirm } from '../components/common/ConfirmDialog'

interface RingGroupMember {
  extension: string
  delay: number
}

interface RingGroup {
  id: string
  name: string
  number: string
  strategy: string
  ring_time: number
  failover_destination_type: string
  failover_destination_id: string
  ringback_mode?: string
  moh_class?: string
  ignore_cf?: boolean
  confirm_calls?: boolean
  skip_busy?: boolean
  is_active: boolean
  members: (string | RingGroupMember)[]  // Backend returns strings, form uses objects
}

const emptyForm = {
  name: '',
  number: '',
  strategy: 'ringall',
  ring_time: 20,
  failover_destination_type: '',
  failover_destination_id: '',
  ringback_mode: 'ringtone',
  moh_class: 'default',
  ignore_cf: true,        // default: keep current dialplan behaviour ('i' flag)
  confirm_calls: false,
  skip_busy: false,
  is_active: true,
  members: [] as RingGroupMember[]
}

const strategies = [
  { value: 'ringall', label: 'Alle gleichzeitig' },
  { value: 'hunt', label: 'Nacheinander' },
  { value: 'memoryhunt', label: 'Nacheinander (mit Erinnerung)' },
  { value: 'firstnotonphone', label: 'Erster freier' },
  { value: 'random', label: 'Zufällig' }
]

// Allowed destination types for ring group failover
const RG_ALLOWED_TYPES = [
  'extension',
  'ring_group',
  'queue',
  'ivr',
  'time_condition',
  'conference',
  'voicemail',
  'parking',
  'night_mode',
  'announcement',
  'external_dest',
  'external',
  'hangup',
]

export default function RingGroups() {
  const confirm = useConfirm()
  const [groups, setGroups] = useState<RingGroup[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingGroup, setEditingGroup] = useState<RingGroup | null>(null)
  const [formData, setFormData] = useState(emptyForm)
  const [extensions, setExtensions] = useState<{extension: string, name: string}[]>([])
  const [mohClasses, setMohClasses] = useState<{name: string}[]>([])

  useEffect(() => {
    loadGroups()
    loadExtensions()
    loadMohClasses()
  }, [])

  const loadGroups = async () => {
    try {
      const response = await ringGroupsApi.list()
      setGroups(response.data.items || [])
    } catch {
      toast.error('Fehler beim Laden')
    } finally {
      setLoading(false)
    }
  }

  const loadExtensions = async () => {
    try {
      const response = await extensionsApi.list()
      setExtensions(response.data.items || [])
    } catch {
      console.error('Error loading extensions')
    }
  }

  const loadMohClasses = async () => {
    try {
      const response = await mohApi.list()
      setMohClasses(response.data.items || [])
    } catch {
      console.error('Error loading MoH classes')
    }
  }

  const openCreateModal = () => {
    setEditingGroup(null)
    setFormData(emptyForm)
    setShowModal(true)
  }

  const openEditModal = (group: RingGroup) => {
    setEditingGroup(group)
    // Convert members from strings or objects to RingGroupMember objects
    const normalizedMembers = (group.members || []).map((m: string | RingGroupMember) => {
      if (typeof m === 'string') {
        return { extension: m, delay: 0 }
      }
      return m
    })
    setFormData({
      name: group.name,
      number: group.number,
      strategy: group.strategy || 'ringall',
      ring_time: group.ring_time || 20,
      failover_destination_type: group.failover_destination_type || '',
      failover_destination_id: group.failover_destination_id || '',
      ringback_mode: group.ringback_mode || 'ringtone',
      moh_class: group.moh_class || 'default',
      ignore_cf: group.ignore_cf ?? true,
      confirm_calls: group.confirm_calls ?? false,
      skip_busy: group.skip_busy ?? false,
      is_active: group.is_active,
      members: normalizedMembers
    })
    setShowModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      if (editingGroup) {
        await ringGroupsApi.update(editingGroup.id, formData)
        toast.success('Rufgruppe aktualisiert')
      } else {
        await ringGroupsApi.create(formData)
        toast.success('Rufgruppe erstellt')
      }
      closeModal()
      loadGroups()
    } catch {
      toast.error('Fehler beim Speichern')
    }
  }

  const handleDelete = async (group: RingGroup) => {
    const confirmed = await confirm({
      title: 'Rufgruppe löschen',
      message: `Rufgruppe "${group.name}" wirklich löschen?`,
      confirmText: 'Löschen',
      variant: 'danger'
    })
    if (!confirmed) return
    try {
      await ringGroupsApi.delete(group.id)
      toast.success('Rufgruppe gelöscht')
      loadGroups()
    } catch {
      toast.error('Fehler beim Löschen')
    }
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingGroup(null)
    setFormData(emptyForm)
  }

  const addMember = () => {
    const availableExt = extensions.find(e => !formData.members.find(m => m.extension === e.extension))
    if (availableExt) {
      setFormData({
        ...formData,
        members: [...formData.members, { extension: availableExt.extension, delay: 0 }]
      })
    }
  }

  const updateMember = (index: number, field: string, value: unknown) => {
    const newMembers = [...formData.members]
    newMembers[index] = { ...newMembers[index], [field]: value }
    setFormData({ ...formData, members: newMembers })
  }

  const removeMember = (index: number) => {
    setFormData({
      ...formData,
      members: formData.members.filter((_, i) => i !== index)
    })
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>Rufgruppen</h4>
        <Button variant="primary" onClick={openCreateModal}>
          <i className="bi bi-plus-lg me-1"></i> Neue Rufgruppe
        </Button>
      </div>

      <div className="table-container">
        <Table hover className="mb-0">
          <thead>
            <tr>
              <th>Nummer</th>
              <th>Name</th>
              <th>Strategie</th>
              <th>Mitglieder</th>
              <th>Status</th>
              <th className="text-end">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-4">Laden...</td></tr>
            ) : groups.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-4 text-muted">Keine Rufgruppen</td></tr>
            ) : (
              groups.map(g => (
                <tr key={g.id}>
                  <td><strong>{g.number}</strong></td>
                  <td>{g.name}</td>
                  <td><Badge bg="info">{strategies.find(s => s.value === g.strategy)?.label || g.strategy}</Badge></td>
                  <td><Badge bg="secondary">{(g.members || []).length}</Badge></td>
                  <td><Badge bg={g.is_active ? 'success' : 'secondary'}>{g.is_active ? 'Aktiv' : 'Inaktiv'}</Badge></td>
                  <td className="text-end">
                    <Button variant="outline-primary" size="sm" className="me-1" onClick={() => openEditModal(g)}>
                      <i className="bi bi-pencil"></i>
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={() => handleDelete(g)}>
                      <i className="bi bi-trash"></i>
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <Modal show={showModal} onHide={closeModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingGroup ? 'Rufgruppe bearbeiten' : 'Neue Rufgruppe'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Nummer *</Form.Label>
                  <Form.Control
                    value={formData.number}
                    onChange={(e) => setFormData({...formData, number: e.target.value})}
                    required
                    placeholder="z.B. 500"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    placeholder="z.B. Vertrieb"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Klingeldauer (Sek.)</Form.Label>
                  <Form.Control
                    type="number"
                    value={formData.ring_time}
                    onChange={(e) => setFormData({...formData, ring_time: parseInt(e.target.value) || 20})}
                    min={5}
                    max={300}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Strategie</Form.Label>
              <Form.Select
                value={formData.strategy}
                onChange={(e) => setFormData({...formData, strategy: e.target.value})}
              >
                {strategies.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Wartemusik während Klingeln</Form.Label>
                  <Form.Select
                    value={formData.ringback_mode}
                    onChange={(e) => setFormData({...formData, ringback_mode: e.target.value})}
                  >
                    <option value="ringtone">Klingelton (Ringback)</option>
                    <option value="moh">Music on Hold</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Was der Anrufer hört, während die Mitglieder klingeln.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>MoH-Klasse</Form.Label>
                  <Form.Select
                    value={formData.moh_class}
                    onChange={(e) => setFormData({...formData, moh_class: e.target.value})}
                    disabled={formData.ringback_mode !== 'moh'}
                  >
                    <option value="default">default</option>
                    {mohClasses.map(m => (
                      <option key={m.name} value={m.name}>{m.name}</option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Nur aktiv wenn "Music on Hold" gewählt ist.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="rg-skip-busy"
                    label="Belegte Mitglieder überspringen"
                    checked={formData.skip_busy}
                    onChange={(e) => setFormData({...formData, skip_busy: e.target.checked})}
                  />
                  <Form.Text className="text-muted">
                    Mitglieder im Gespräch werden gar nicht erst angerufen.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="rg-ignore-cf"
                    label="Rufumleitungen ignorieren"
                    checked={formData.ignore_cf}
                    onChange={(e) => setFormData({...formData, ignore_cf: e.target.checked})}
                  />
                  <Form.Text className="text-muted">
                    Wenn aus, kann eine Mitglieds-Rufumleitung den ganzen Ring leiten.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="rg-confirm-calls"
                    label="Anruf-Bestätigung verlangen"
                    checked={formData.confirm_calls}
                    onChange={(e) => setFormData({...formData, confirm_calls: e.target.checked})}
                  />
                  <Form.Text className="text-muted">
                    Mitglied muss "1" drücken um den Anruf anzunehmen (für Mobil-Mailboxen).
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Card className="mb-3">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <span><i className="bi bi-people me-2"></i>Mitglieder</span>
                <Button variant="outline-primary" size="sm" onClick={addMember} disabled={formData.members.length >= extensions.length}>
                  <i className="bi bi-plus-lg me-1"></i> Mitglied hinzufügen
                </Button>
              </Card.Header>
              <Card.Body>
                {formData.members.length === 0 ? (
                  <p className="text-muted mb-0">Keine Mitglieder. Klicken Sie auf "Mitglied hinzufügen".</p>
                ) : (
                  formData.members.map((member, index) => (
                    <Row key={index} className="mb-2 align-items-end">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="small">Nebenstelle</Form.Label>
                          <Form.Select
                            value={member.extension}
                            onChange={(e) => updateMember(index, 'extension', e.target.value)}
                          >
                            {extensions.map(ext => (
                              <option key={ext.extension} value={ext.extension}>{ext.extension} - {ext.name}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label className="small">Verzögerung (Sek.)</Form.Label>
                          <Form.Control
                            type="number"
                            value={member.delay}
                            onChange={(e) => updateMember(index, 'delay', parseInt(e.target.value) || 0)}
                            min={0}
                            max={60}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Button variant="outline-danger" size="sm" onClick={() => removeMember(index)}>
                          <i className="bi bi-trash"></i>
                        </Button>
                      </Col>
                    </Row>
                  ))
                )}
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Header>
                <i className="bi bi-arrow-return-right me-2"></i>Bei keiner Antwort (Failover)
              </Card.Header>
              <Card.Body>
                <Form.Group>
                  <Form.Label>Ziel</Form.Label>
                  <DestinationSelector
                    destinationType={formData.failover_destination_type}
                    destinationValue={formData.failover_destination_id}
                    onChange={(type, value) => setFormData({
                      ...formData,
                      failover_destination_type: type,
                      failover_destination_id: value
                    })}
                    allowedTypes={RG_ALLOWED_TYPES}
                    excludeType="ring_group"
                    excludeId={editingGroup?.id}
                    allowExternal={true}
                    placeholder="Failover-Ziel suchen..."
                  />
                </Form.Group>
              </Card.Body>
            </Card>

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="is_active"
                label="Aktiv"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>Abbrechen</Button>
            <Button variant="primary" type="submit">
              {editingGroup ? 'Speichern' : 'Erstellen'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  )
}
